import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const { hasRole } = usePermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Inspector-only: primary role is inspector with no secondary roles
  const isInspectorOnly =
    user?.role === 'inspector' && !user?.secondaryRoles?.length;
  const isAccessingDashboard = location.pathname.startsWith('/dashboard');

  // Redirect inspector-only users to /lite if they try to access /dashboard
  if (isInspectorOnly && isAccessingDashboard) {
    return <Navigate to="/lite" replace />;
  }

  // If specific roles are required, check primary + secondary roles
  if (allowedRoles && allowedRoles.length > 0) {
    const userHasAllowedRole = allowedRoles.some((r) => hasRole(r));
    if (!userHasAllowedRole) {
      if (isInspectorOnly) {
        return <Navigate to="/lite" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
